import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export function LanguageSelector() {
  const { locale, setLocale, supportedLocales, localeLabels, localeFlags } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Only one language right now — hide the selector until more are added
  if (supportedLocales.length <= 1) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px',
          background: 'rgba(255,255,255,0.05)',
          border: 'var(--border-subtle)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--chalk-dim)',
          fontSize: '0.75rem', fontWeight: 500,
          cursor: 'pointer',
          transition: 'all var(--transition-fast)',
        }}
      >
        <span>{localeFlags[locale] ?? '🌐'}</span>
        <span>{localeLabels[locale] ?? locale}</span>
        <span style={{ fontSize: '0.6rem', opacity: 0.6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: 'var(--glass-bg-dark)',
          backdropFilter: 'var(--glass-blur)',
          WebkitBackdropFilter: 'var(--glass-blur)',
          border: 'var(--border-dim)',
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden', zIndex: 200,
          minWidth: 130,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          animation: 'fadeIn 0.15s ease',
        }}>
          {supportedLocales.map(l => (
            <button
              key={l}
              onClick={() => { setLocale(l); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '9px 14px',
                background: l === locale ? 'rgba(201,168,76,0.1)' : 'transparent',
                border: 'none',
                color: l === locale ? 'var(--gold)' : 'var(--chalk-dim)',
                fontSize: '0.78rem', fontWeight: l === locale ? 600 : 400,
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span>{localeFlags[l] ?? '🌐'}</span>
              <span>{localeLabels[l] ?? l}</span>
              {l === locale && <span style={{ marginLeft: 'auto', fontSize: '0.65rem' }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}